// src/components/tools/FlyToToolPanel.jsx
//
// Horizontal pill anchored next to the FlyTo rail button. Height matches the
// rail button (~29px). Single text input + Go. No header, no helper text;
// error state is conveyed by a red bottom-border on the input plus a `title`
// tooltip carrying the message.
//
// Auto-focuses on mount so the user can start typing immediately.
// Enter submits (form onSubmit); Go is the secondary trigger.
// Go is disabled when the input is empty.
// Closes the slide-out automatically on success.

import { useState, useRef, useEffect } from "react";
import GloMatchModal from "./GloMatchModal";
import { gloMatchName } from "../../hooks/useFlyTo";

export default function FlyToToolPanel({ onFlyToPlace, onFlyToBounds, onClose }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  // Set when a block+section names more than one place. The camera has NOT
  // moved at this point -- useFlyTo returns without flying so the choice is
  // the user's, and this holds the candidates until they make it.
  const [ambiguous, setAmbiguous] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!query.trim() || !onFlyToPlace) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setSearching(true);
    setError(null);

    const r = await onFlyToPlace(query, { signal: ctrl.signal });

    setSearching(false);
    if (r.ok && r.ambiguous) { setAmbiguous(r); return; }
    if (r.ok) { onClose?.(); return; }
    if (r.reason === "aborted") return;
    setError(r.reason === "not_found" ? "No matches found" : "Search failed");
  }

  function pickMatch(m) {
    setAmbiguous(null);
    // fitBounds on the section's own envelope rather than flyTo on its
    // centroid: a section is an area, and its extent is what the user asked
    // to see. Falls back to nothing if the bbox is malformed -- onFlyToBounds
    // itself refuses anything that is not length 4.
    onFlyToBounds?.(m.bbox, { maxZoom: 15 });
    onClose?.();
  }

  return (
    <>
      <form
      onSubmit={handleSubmit}
      className="h-[29px] flex items-center gap-1 px-1.5
                 rounded-sm border border-accent-info/60 bg-white/95 shadow-md"
    >
      <input
        type="text"
        autoFocus
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (error) setError(null);
        }}
        placeholder="Fly to…"
        aria-label="Place or coordinates"
        title={error ?? undefined}
        className={
          "w-[180px] min-w-0 bg-transparent px-1 py-0 text-[11px] text-slate-700 " +
          "placeholder:text-slate-400 focus:outline-none border-b transition " +
          (error
            ? "border-red-500"
            : "border-transparent focus:border-accent-info/60")
        }
      />
      <button
        type="submit"
        disabled={searching || !query.trim()}
        className="px-2.5 h-[21px] text-[11px] font-semibold rounded leading-none transition
                   border border-accent-action/70 bg-accent-action text-slate-900
                   hover:bg-accent-action/90
                   disabled:bg-slate-300 disabled:border-slate-400
                   disabled:text-slate-500 disabled:cursor-not-allowed"
      >
        {searching ? "…" : "Go"}
      </button>
      </form>

      {ambiguous && (
        <GloMatchModal
          title={ambiguous.name}
          matches={ambiguous.matches}
          truncated={ambiguous.truncated}
          onPick={pickMatch}
          onClose={() => setAmbiguous(null)}
        />
      )}
    </>
  );
}
